import { type LegacyRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    GA4FeatureCategory,
    PromotionType,
    trackAnalyticsEvent,
    trackEecPromotionClick,
    trackEecPromotionImpression,
} from '@jsmdg/tracking';
import { Image, PureLink, Slider, SlidingDirections, Wysiwyg } from '@jsmdg/yoshi';
import { useHighestMatchingBreakpoint } from '../../../hooks/useHighestMatchingBreakpoint';
import { useImpressionTracking } from '../../../hooks/useImpressionTracking';
import { type ProductDetailContext } from '../../../types/types';
import { pageSliderConfig } from './pageSliderConfig';
import { type PageTile, pageTilesJS, pageTilesMD } from './pageTiles';
import styles from './PageRecommendations.module.scss';

const messagesSlider = defineMessages({
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const trackSlide = (direction: string, category: string) => {
    trackAnalyticsEvent({
        category,
        action: 'Slide',
        label: direction,
    });
};

const promotionType = 'Recommendation Shopping';

type PageSlideProps = {
    readonly pageTile: PageTile;
    readonly position: number;
};

const PageSlide = ({ pageTile, position }: PageSlideProps): JSX.Element => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const slot = `vertical1_horizonal${position.toString()}`;

    const promotionTrackingPayload = {
        id: pageTile.title,
        name: pageTile.title,
        creative: `Page_Tile`,
        position: slot,
    };

    const promotionTrackingEventData = {
        featureCategory: GA4FeatureCategory.ProductDetailInformation,
        creativeName: 'Page_Tile',
        creativeSlot: slot,
        promotionName: pageTile.title,
        promotionId: pageTile.trackingName,
        locationId: pageTile.url,
        positionVertical: '1',
        positionHorizontal: position.toString(),
        promotionType: PromotionType.Static,
    };

    const onPageClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        event?.preventDefault();

        trackEecPromotionClick(promotionTrackingPayload, promotionTrackingEventData);
        window.location.href = pageTile.url;
    };

    const setImpressionTrackedElement = useImpressionTracking(() =>
        trackEecPromotionImpression([promotionTrackingPayload], promotionTrackingEventData),
    );

    return (
        <PureLink
            href={pageTile.url}
            className={classNames(
                'd-flex flex-column align-items-center align-items-xs-start text-decoration-none w-100',
                styles.pageTile,
            )}
            ref={setImpressionTrackedElement as LegacyRef<HTMLAnchorElement> | undefined}
            onClick={onPageClick}
        >
            <div
                className={classNames(
                    styles.pageTileImageWrapper,
                    'position-relative overflow-hidden mb-1x mb-sm-2x',
                )}
            >
                <Image
                    src={pageTile.image.url}
                    alt={pageTile.title}
                    className={classNames(styles.pageTileImage, 'w-100 h-100')}
                    onLoad={() => setImageLoaded(true)}
                />
                {!imageLoaded && (
                    <div
                        className={classNames(
                            styles.imagePlaceholer,
                            'position-absolute top-0 bottom-0 start-0 end-0',
                        )}
                    />
                )}
            </div>
            <h2 className={classNames(styles.pageTileLabel, 'text-center text-xs-left')}>
                <Wysiwyg content={pageTile.title} />
            </h2>
        </PureLink>
    );
};

const PageRecommendations = (): JSX.Element | null => {
    const intl = useIntl();
    const { isMydays } = useFragmentContext<ProductDetailContext>();

    const highestMatchingBreakpoint = useHighestMatchingBreakpoint();
    const sliderConfig = pageSliderConfig[highestMatchingBreakpoint];

    const pageTiles = isMydays ? pageTilesMD : pageTilesJS;

    const slides = pageTiles.map((pageTile: PageTile, index: number) => {
        return {
            key: pageTile.title,
            content: <PageSlide position={index + 1} pageTile={pageTile} />,
        };
    });

    return (
        <div className={styles.sliderWrapper}>
            <Slider
                a11yNextSlide={intl.formatMessage(messagesSlider.a11yNextSlide)}
                a11yPreviousSlide={intl.formatMessage(messagesSlider.a11yPreviousSlide)}
                key="non-virtual"
                slides={slides}
                config={sliderConfig}
                handleShowNext={() => trackSlide(SlidingDirections.Next, promotionType)}
                handleShowPrevious={() => trackSlide(SlidingDirections.Previous, promotionType)}
                handleTouchStart={() => trackSlide('', promotionType)}
                handleTouchMove={() => trackSlide('', promotionType)}
                classes={{
                    swiperSlide: styles.swiperSlide,
                }}
            />
        </div>
    );
};

export { PageRecommendations };
